import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import type { UserProfile } from "../lib/auth";
import { getTeam, subscribeTeam } from "../data/team";
import { completeSession, logEvent, hasRated } from "../lib/state";
import { loadProgress, clearProgress } from "../lib/progress";
import { notify } from "../lib/api";
import { blobToBase64 } from "../lib/certificate";
import { generateSummaryPdfBlob, downloadSummaryPdf } from "../lib/summaryPdf";
import { getVisibleSystems } from "../data/systems";
import { getQAForPath } from "../data/qa";
import { getChecklist } from "../data/checklist";
import { getCareerPaths } from "../data/careerPaths";
import { KADEN_GATE_URL, VIVA_ENGAGE_URL, ITSM_URL } from "../lib/contacts";
import { RatingGate } from "../components/RatingGate";
import { formatDuration } from "../admin/lib/format";

interface CertificateScreenProps {
  lang: Lang;
  profile: UserProfile | null;
  careerPath: string;
  onRestart: () => void;
  onBack: () => void;
}

const CELEBRATE_COLORS = ["#dab87a", "#2d7a4a", "#1a2820", "#ece8df"];

function celebrate() {
  if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const end = Date.now() + 1200;
  (function frame() {
    // zIndex must clear .kd-rating-gate's 1500 — canvas-confetti defaults to
    // 100, which put its canvas behind the rating overlay that shows right
    // after completion.
    confetti({ particleCount: 3, angle: 60, spread: 70, origin: { x: 0 }, colors: CELEBRATE_COLORS, zIndex: 2000 });
    confetti({ particleCount: 3, angle: 120, spread: 70, origin: { x: 1 }, colors: CELEBRATE_COLORS, zIndex: 2000 });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();

  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = ctx.currentTime + i * 0.12;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.08, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.6);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.6);
      });
      setTimeout(() => {
        try {
          ctx.close();
        } catch {
          /* ignore */
        }
      }, 1500);
    }
  } catch {
    /* ignore */
  }
}

/** Step 8 — completion certificate, downloads, next steps, IT team, feedback. */
export function CertificateScreen({ lang, profile, careerPath, onRestart, onBack }: CertificateScreenProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const name = profile?.name || "";
  const email = profile?.email || (typeof window !== "undefined" && sessionStorage.getItem("kd_email")) || "";
  const role = profile?.role || "";
  const [showRating, setShowRating] = useState(() => !hasRated(email || "anonymous"));
  const [team, setTeam] = useState(() => getTeam());
  useEffect(() => subscribeTeam(() => setTeam(getTeam())), []);

  const careerPathLabel = getCareerPaths().find((p) => p.id === careerPath)?.[lang === "ar" ? "ar" : "en"] || careerPath;

  /** Gather everything the employee read this journey — same filters the
   *  Systems/Q&A steps used — for the summary PDF (download + email). */
  function summaryOpts(date: string) {
    const systems = getVisibleSystems().filter((s) => s.paths.includes("all") || s.paths.includes(careerPath));
    const qa = getQAForPath(careerPath);
    const pendingIds = email ? loadProgress(email)?.pendingItems || [] : [];
    return { lang, profile, careerPathLabel, date, systems, qa, checklist: getChecklist(), pendingIds };
  }

  useEffect(() => {
    // Read any deferred checklist items before clearing saved progress — they
    // go into the manager notification below so IT/the manager can follow up
    // without the employee having to remember to flag anything themselves.
    const pendingIds = email ? loadProgress(email)?.pendingItems || [] : [];
    const checklist = getChecklist();
    const pendingLabels = pendingIds
      .map((id) => checklist.find((c) => c.id === id))
      .filter((c): c is (typeof checklist)[number] => !!c)
      .map((c) => (lang === "ar" ? c.ar : c.en));
    const pendingRowHtml =
      pendingLabels.length > 0
        ? `<tr><td style="color:#b3402a;vertical-align:top;"><strong>${t("بنود معلّقة", "Pending items")}</strong></td><td style="color:#b3402a;">${pendingLabels.map((l) => `• ${l}`).join("<br>")}</td></tr>`
        : "";

    const completedSession = completeSession(email || "anonymous");
    if (email) clearProgress(email);
    logEvent("journey_completed", { email, name, meta: { careerPath, pendingItems: pendingIds.join(",") } });
    celebrate();

    // Notify the manager, and email the completion certificate to the employee.
    const date = new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US");
    const durationMs = completedSession?.completedAt && completedSession.startedAt
      ? completedSession.completedAt - completedSession.startedAt
      : undefined;
    notify("manager", {
      to: profile?.managerEmail || undefined,
      lang,
      vars: { NAME: name, ROLE: role, PATH: careerPathLabel, DATE: date, MANAGER_NAME: profile?.manager || "", PENDING_ROW: pendingRowHtml, DURATION: formatDuration(durationMs, lang) },
    } as any)?.catch?.(() => {});

    (async () => {
      if (!email) return;
      try {
        const pdf = await generateSummaryPdfBlob(summaryOpts(date));
        const attachments = [
          ...(pdf ? [{ filename: "Kaden-Onboarding-Summary.pdf", contentBase64: await blobToBase64(pdf), contentType: "application/pdf" }] : []),
        ];
        await notify("completion", { to: email, lang, vars: { NAME: name, ROLE: role, PATH: careerPath, DATE: date }, attachments });
      } catch (err) {
        console.warn("[completion-email] failed", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextSteps: Array<{ icon: string; ar: string; en: string; url?: string }> = [
    { icon: "📧", ar: "تحقّق من بريدك الإلكتروني لأي دعوات أو تعليمات إضافية", en: "Check your email for additional invitations or instructions" },
    { icon: "🏠", ar: "ادخل إلى Kaden Gate واستكشف البوابة الداخلية", en: "Visit Kaden Gate and explore the internal portal", url: KADEN_GATE_URL },
    { icon: "💬", ar: "انضم إلى مجتمع كادن على Viva Engage", en: "Join the Kaden community on Viva Engage", url: VIVA_ENGAGE_URL },
    { icon: "🛠️", ar: "احفظ رابط بوابة الدعم الفني ITSM", en: "Bookmark the ITSM IT support portal", url: ITSM_URL },
  ];

  return (
    <div className="kd-step-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="glass-panel step-completion">
        <div className="kd-step-header">
          <h1>{t("تهانينا، لقد أتممت رحلة التأهيل", "Congratulations, you have completed the onboarding journey")}</h1>
          <p>{t("أنت الآن جاهز لبدء العمل في كادن للاستثمار. راجع الخطوات التالية.", "You are now ready to begin working at Kaden Investment. Review the next steps below.")}</p>
        </div>

        <div className="kd-download-buttons">
          <button className="kd-btn-primary kd-btn-gold kd-btn-pdf" onClick={() => downloadSummaryPdf(summaryOpts(new Date().toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")))}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <span>{t("تحميل ملخص الرحلة (PDF)", "Download Journey Summary (PDF)")}</span>
          </button>
        </div>

        <div className="kd-reassurance-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <p>{t("لا داعي للقلق، جميع الأنظمة والأدوات المذكورة يمكنك الوصول إليها في أي وقت من خلال بوابة Kaden Gate.", "No need to worry, all the systems and tools mentioned are accessible anytime through the Kaden Gate portal.")}</p>
        </div>

        <div className="kd-next-section">
          <h2>{t("الخطوات التالية", "Next Steps")}</h2>
          <div className="kd-next-grid">
            {nextSteps.map((s, i) => (
              <div className="kd-next-card" key={i}>
                <span className="kd-next-icon">{s.icon}</span>
                <span className="kd-next-text">{lang === "ar" ? s.ar : s.en}</span>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="kd-next-link">
                    {t("فتح", "Open")} ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="kd-it-team-section">
          <h2>{t("فريق تقنية المعلومات والرقمنة", "IT & Digitalization Team")}</h2>
          <p className="kd-it-team-subtitle">
            {t(
              "نحن هنا لدعمك. لا تتردد في التواصل معنا لأي مساعدة تقنية عبر البريد الإلكتروني الموحد it@kaden-sa.com أو التحويلة الداخلية لقسم تقنية المعلومات 310.",
              "We are here to support you. Do not hesitate to reach out for any technical assistance via our shared email it@kaden-sa.com or the IT department's internal extension 310.",
            )}
          </p>
          <div className="kd-it-team-grid">
            {team.map((m) => (
              <div className="kd-it-member-card" key={m.id}>
                {m.photo && (
                  <img
                    src={m.photo}
                    alt={lang === "ar" ? m.nameAr : m.nameEn}
                    className="kd-it-member-photo"
                    loading="lazy"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                  />
                )}
                <div className="kd-it-member-info">
                  <h3 className="kd-it-member-name">{lang === "ar" ? m.nameAr : m.nameEn}</h3>
                  <p className="kd-it-member-title">{lang === "ar" ? m.titleAr : m.titleEn}</p>
                  <p className="kd-it-member-desc">{lang === "ar" ? m.descAr : m.descEn}</p>
                  <div className="kd-it-member-contact">
                    {m.email && (
                      <a href={`mailto:${m.email}`} className="kd-it-member-link">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="4" width="20" height="16" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span>{m.email}</span>
                      </a>
                    )}
                    {m.ext && (
                      <span className="kd-it-member-ext">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.7 19.79 19.79 0 0 1 1.61 5.1 2 2 0 0 1 3.58 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.91a16 16 0 0 0 6.06 6.06l1.97-1.97a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        <span>{t("تحويلة", "Ext.")} {m.ext}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="kd-completion-nav">
          <button className="kd-btn-secondary" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={lang === "ar" ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
            </svg>
            <span>{t("العودة للخطوة السابقة", "Go to previous step")}</span>
          </button>
          <button className="kd-btn-secondary" onClick={onRestart}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
            <span>{t("إعادة الرحلة من البداية", "Restart the journey")}</span>
          </button>
        </div>
      </div>

      {showRating && <RatingGate lang={lang} email={email} name={name} onClose={() => setShowRating(false)} />}
    </div>
  );
}
